import { AccountRole, PermissionsEnum, LocationRoles, LocationRole, UserRole } from 'model/Role';
import { union, isEmpty } from 'lodash';

export interface LocationRolePayload {
  locationId: string;
  roles: string[];
  inherited?: LocationRolePayload[];
}

export interface AccountRolePayload {
  accountId: string;
  roles: string[];
}

export interface UserRolePayload {
  userId: string;
  roles: string[];
  inherited?: LocationRolePayload[];
}

export interface RolesPayload {
  roles: string[];
}

export const fromPermission = (permission: PermissionsEnum): string => {
  switch(permission) {
    case PermissionsEnum.OWNER:
      return 'owner';
    case PermissionsEnum.READONLY:
      return 'readonly';
    case PermissionsEnum.PROVISION:
      return 'provision';
    case PermissionsEnum.USER_CREATE:
      return 'user-create';
    case PermissionsEnum.USER_REMOVE:
      return 'user-remove';
    case PermissionsEnum.USER_UPDATE:
      return 'user-update';
    case PermissionsEnum.VALVE_CLOSE:
      return 'valve-close';
    case PermissionsEnum.VALVE_OPEN:
      return 'valve-open';
    case PermissionsEnum.WRITE:
      return 'write';
  }
}

export const toPermission = (role: string): PermissionsEnum => {
  switch(role) {
    case 'owner':
      return PermissionsEnum.OWNER;
    case 'readonly':
      return PermissionsEnum.READONLY;
    case 'user-create':
      return PermissionsEnum.USER_CREATE;
    case 'user-remove':
      return PermissionsEnum.USER_REMOVE;
    case 'user-update':
      return PermissionsEnum.USER_UPDATE;
    case 'valve-close':
      return PermissionsEnum.VALVE_CLOSE;
    case 'valve-open':
      return PermissionsEnum.VALVE_OPEN;
    case 'write':
      return PermissionsEnum.WRITE;
    case 'provision':
      return PermissionsEnum.PROVISION;
    default:
      return PermissionsEnum.READONLY;
  }
}

export const fromAccountRolesToInvite = (accountRole?: AccountRole): string[] => accountRole?.roles.map(role => fromPermission(role)) || [];
export const fromLocationRoles = (locationRoles?: LocationRoles): LocationRolePayload[] => locationRoles?.map(r => ({
  locationId: r.locationId,
  roles: r.roles.map(role => fromPermission(role))
})) || [];

export const toAccountRole = (accountRole?: AccountRolePayload): AccountRole | undefined => accountRole ? ({
  accountId: accountRole.accountId || '',
  roles: accountRole?.roles.map(r => toPermission(r)) || []
}): undefined;

const getInheritedRoles = (inherited: LocationRolePayload[]): PermissionsEnum[] => {
  return inherited.reduce((acc: PermissionsEnum[], value) => {
    return union(acc, value.roles.map(role => toPermission(role)))
  }, []);
}

export const toLocationRole = (locationRole?: LocationRolePayload[]): LocationRole[] | undefined => locationRole?.map(r => {
  const inherited = r.inherited ? getInheritedRoles(r.inherited) : [];
  return {
    locationId: r.locationId,
    roles: union(r.roles.map(role => toPermission(role)), inherited),
    isInherited: !isEmpty(inherited) && isEmpty(r.roles),
  }
});

export const fromAccountRoles = (accountRole?: AccountRole): AccountRolePayload | undefined => accountRole ? ({
  accountId: accountRole.accountId,
  roles: accountRole.roles.map(r => fromPermission(r))
}): undefined;

export const fromPermissionEnumToString = (permissions: PermissionsEnum[]): string[] => permissions.map(p => fromPermission(p));

export const toUserRole = (userRoles: UserRolePayload[], userId: string): UserRole => {
  const userRole = userRoles.find(ur => ur.userId === userId);
  const inherited = userRole?.inherited ? getInheritedRoles(userRole.inherited) : [];
  return {
    userId,
    roles: union(userRole?.roles.map(role => toPermission(role)) || [], inherited),
    isInherited: !isEmpty(inherited) && isEmpty(userRole?.roles),
  }
};